import { firestore } from '@src/api/Firebase/Firebase';
import { collection, Timestamp } from 'firebase/firestore';
import { UserType } from '@api/Users/User';

/**
 * Normaliza un teléfono a los 10 dígitos que este sistema usa como identidad
 * del cliente (México; Twilio le antepone +52 al enviar).
 *
 * Existe porque `customers.phone` es la LLAVE de búsqueda y de login, y las
 * queries de Firestore comparan el string EXACTO: "221 364 5546" nunca iguala
 * a "2213645546". Eso produjo clientes invisibles para el POS (caso Fanny
 * Acosta, 2026-08-19) y 153 clientes duplicados, cada uno con su propia
 * cartera de puntos. Todo lo que capture, busque o guarde un teléfono debe
 * pasar por aquí.
 *
 * ⚠️ PARIDAD: réplica exacta en krispis-web (`api/Customers/Customer.ts`) —
 * misma colección, misma llave; cambiar una exige cambiar la otra.
 */
export const normalizePhone = (phone?: string): string => {
    const digits = String(phone || '').replace(/\D/g, '');
    // "+52 221..." o "52221..." → los 10 locales; un número corto se deja tal
    // cual para que la validación de longitud lo rechace con un mensaje claro
    return digits.length > 10 ? digits.slice(-10) : digits;
};

export interface AddressComponent {
    streetNumber?: string;
    route?: string;
    locality?: string;
    administrativeArea?: string;
    country?: string;
    postalCode?: string;
}

export interface GoogleAddress {
    id: string;
    alias: string;
    formattedAddress: string;
    googlePlaceId?: string;
    addressComponents?: AddressComponent;
    coordinates?: {
        lat: number;
        lng: number;
    };
    isDefault: boolean;
    createdAt: Timestamp;
}

export enum PointTransactionType {
    Earned = 'Earned',
    Spent = 'Spent',
    Initial = 'Initial'
}

export enum PointTransactionSource {
    QR = 'qr',
    Order = 'Order',
    Stamp = 'Stamp',
    Manual = 'Manual',
    Promotion = 'Promotion',
    GiftCard = 'GiftCard',
    System = 'System',
    Warranty = 'Warranty',
    Other = 'Other'
}

export interface PointTransaction {
    date: Timestamp;
    points: number;
    type: PointTransactionType;
    source: PointTransactionSource;
    referenceId?: string;
    description: string;
    createdBy?: UserType;
}

export interface CustomerWallet {
    currentPoints: number;
    transactions: PointTransaction[];
}

export interface CustomerProfile {
    appVersion?: string;
    status?: boolean;
    firstName?: string;
    lastName?: string;
    name?: string;
    photo?: string;
    wallet?: CustomerWallet;
    addresses?: GoogleAddress[];
    birthdayDay?: number;
    birthdayMonth?: number;
    birthdayYear?: number;
}

export interface Customer {
    uid?: string;
    createdAt?: Timestamp;
    profile?: CustomerProfile;
    email?: string;
    phone?: string;
    stripeCustomerId?: string;
    /**
     * ¿El teléfono se comprobó alguna vez con un código (OTP)?
     *
     * Lo escriben krispis-web y el server desde el login identifier-first (plan
     * Wallet, D4): un alta directa con contraseña NO comprueba el número. Aquí
     * se declara por PARIDAD DE SCHEMA — el admin no lo usa todavía; si algún
     * día el POS quiere distinguir clientes sin verificar, el dato ya está.
     * ⚠️ **Campo AUSENTE = verificado** (las cuentas viejas llegaron por OTP).
     */
    phoneVerified?: boolean;
    /** Sucursal cuyo QR/NFC de mostrador dio de alta al cliente. Solo atribución. */
    joinedFromBranchId?: string;
}

export const CUSTOMERS_COLLECTION = 'customers';

export const customersRef = collection(firestore, CUSTOMERS_COLLECTION);
